import React, { useEffect, useRef } from "react";

// Drops leaking up from behind a button (the shop's buy buttons, the start
// page's Play Now button). A tiny DOM emitter:
// while the card is hovered it spawns small short-lived drops; while the
// button itself is hovered it spawns bigger, faster, higher-flying ones at a
// higher rate. Every spawn checks live :hover state (no tracked flags to go
// stale), and when the pointer leaves, spawning simply stops - drops already
// in flight finish their one-shot animation and remove themselves.
//
// The look (colour, the drop's own size) is the caller's: it passes the class of
// the host and of a drop. `scale` stretches how far and how big they go and
// `density` how many there are, for a button much larger than a shop card's.
export default function DropEmitter({ hostClass, dropClass, scale = 1, density = 1, slowOnly = false }: { hostClass: string; dropClass: string; scale?: number; density?: number; slowOnly?: boolean }) {
  const hostRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const wrap = host.parentElement;
    const slot = host.closest("[data-cardslot]");
    if (!wrap || !slot) return;
    let fastTimer: number | null = null;
    const spawn = (fast: boolean) => {
      const d = document.createElement("span");
      d.dataset.fast = fast ? "1" : "0";
      d.className = dropClass;
      d.style.left = `${8 + Math.random() * 84}%`;
      d.style.setProperty("--dx", `${((Math.random() * 2 - 1) * (fast ? 18 : 4) * scale).toFixed(1)}px`);
      // Two characters: card drops are tiny, drift up slowly and die young;
      // button drops are only slightly bigger, live longer and fly higher at
      // a brisk (not frantic) pace. Both fade and shrink linearly from the
      // moment they spawn (see the drop-rise keyframes).
      d.style.setProperty("--rise", `${((fast ? 50 + Math.random() * 23 : 22 + Math.random() * 10) * scale).toFixed(0)}px`);
      d.style.setProperty("--dsc", ((fast ? 0.4 + Math.random() * 0.12 : 0.32 + Math.random() * 0.12) * scale).toFixed(2));
      d.style.animationDuration = `${(fast ? 0.56 + Math.random() * 0.23 : 0.6 + Math.random() * 0.7).toFixed(2)}s`;
      d.addEventListener("animationend", () => d.remove());
      host.appendChild(d);
    };
    // Independent loops: the card set is THREE separate slow spawners, each
    // on its own widely jittered clock, so their spawns drift in and out of
    // phase and sometimes land almost (or exactly) together, the way real
    // bubbling does. The button's fast set runs on top of them. Population
    // caps per set.
    const count = (fast: boolean) => host.querySelectorAll(`[data-fast="${fast ? 1 : 0}"]`).length;
    const SLOW_LOOPS = 3;
    const slowTimers: (number | null)[] = Array(SLOW_LOOPS).fill(null);
    const tickSlow = (i: number) => {
      if (!slot.matches(":hover")) { slowTimers[i] = null; return; }
      if (count(false) < 6 * density) spawn(false);
      slowTimers[i] = window.setTimeout(() => tickSlow(i), (390 + Math.random() * 565) / density);
    };
    const tickFast = () => {
      if (!wrap.matches(":hover")) { fastTimer = null; return; }
      if (count(true) < 30 * density) spawn(true);
      fastTimer = window.setTimeout(tickFast, 24 / density);
    };
    const startSlow = () => {
      for (let i = 0; i < SLOW_LOOPS; i++) {
        // stagger the first spawn of each loop so they don't start in sync
        if (slowTimers[i] == null) slowTimers[i] = window.setTimeout(() => tickSlow(i), Math.random() * 400);
      }
    };
    // `slowOnly`: the gentle set alone, wherever the pointer is on the card.
    const startFast = () => { if (!slowOnly && fastTimer == null) tickFast(); };
    slot.addEventListener("mouseenter", startSlow);
    wrap.addEventListener("mouseenter", startFast);
    return () => {
      slot.removeEventListener("mouseenter", startSlow);
      wrap.removeEventListener("mouseenter", startFast);
      for (const t of slowTimers) if (t != null) clearTimeout(t);
      if (fastTimer != null) clearTimeout(fastTimer);
      host.textContent = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <span ref={hostRef} className={hostClass} />;
}
